import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userService } from 'components/api/users';
import { User, UsersParams, UsersResponse } from 'types/user';

interface UsersState {
  data: User[];
  total: number;
  isLoading: boolean;
  error: string | null;
  params: UsersParams;
  userDetail: User | null;
  isLoadingSmall: boolean;
}

const initialState: UsersState = {
  data: [],
  total: 0,
  isLoading: false,
  error: null,
  params: {
    page: 1,
    limit: 10,
    sortField: 'id',
    sortValue: 'DESC',
    siteId: 1,
  },
  userDetail: null,
  isLoadingSmall: false,
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params: UsersParams, { rejectWithValue }) => {
    try {
      const response = await userService.getUsers(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch users');
    }
  },
);

export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await userService.getUserById(id);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch user');
    }
  },
);

export const verifyEmail = createAsyncThunk(
  'users/verifyEmail',
  async (id: string | number, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await userService.verifyEmail(id);
      dispatch(fetchUserById(id.toString()));
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to verify email',
      );
    }
  },
);

export const verifyPhone = createAsyncThunk(
  'users/verifyPhone',
  async (id: string | number, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await userService.verifyPhone(id);
      dispatch(fetchUserById(id.toString()));
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to verify phone',
      );
    }
  },
);

export const blockUserThunk = createAsyncThunk(
  'users/blockUser',
  async (
    { id, durationHours, reason }: { id: string | number; durationHours: number; reason: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await userService.blockUser(id, { durationHours, reason });
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to block user');
    }
  },
);

export const unblockUserThunk = createAsyncThunk(
  'users/unblockUser',
  async (id: string | number, { rejectWithValue, dispatch }) => {
    try {
      const response = await userService.unblockUser(id);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to unblock user',
      );
    }
  },
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setParams: (state, action: PayloadAction<Partial<UsersParams>>) => {
      state.params = { ...state.params, ...action.payload };
    },
    resetParams: (state) => {
      state.params = initialState.params;
    },
    clearUserDetail: (state) => {
      state.userDetail = null;
      state.error = null;
    },
    setUserDetail: (state, action: PayloadAction<User>) => {
      state.userDetail = action.payload;
    },
    setUserDetailError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setLoadingSmall: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSmall = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<UsersResponse>) => {
        state.isLoading = false;
        state.data = action.payload.data?.docs || [];
        state.total = action.payload.data?.totalDocs || 0;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserById.pending, (state) => {
        state.isLoadingSmall = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoadingSmall = false;
        state.userDetail = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.isLoadingSmall = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setParams,
  resetParams,
  clearUserDetail,
  setUserDetail,
  setUserDetailError,
  setLoadingSmall,
} = usersSlice.actions;

export default usersSlice.reducer;
